import { Fragment, useMemo, useState } from 'react';
import { NormalizedTask } from '../../domain/normalize';
import {
  cargaReal, countBy, revisionKpis,
  progresoProyectos, serieMensualPorProyecto, serieMensualPorTipo,
  monthIndex, monthShortLabel, ProyectoProgreso,
} from '../../domain/metrics';
import { esEntregado } from '../../domain/config';
import {
  UserDoc, STATUS_LABELS, STATUS_COLORS, PROYECTO_STATUS_LABELS, PROYECTO_STATUS_COLORS,
} from '../../domain/types';
import { nameByEmail, fmtDia, fmtDate } from '../../domain/util';
import {
  Card, Kpi, DonutCard, BoardIntro, ExportButton,
  PALETTE, tooltipStyle, axisTick, GRID, CHART_CURSOR,
} from './shared';
import {
  BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, ReferenceLine,
} from 'recharts';

const DONE_COLOR = '#16a34a';
const PENDING_COLOR = '#cbd5e1';
const OTROS_COLOR = '#94a3b8';
const DIFICULTAD_COLORS: Record<string, string> = { baja: '#16a34a', media: '#f59e0b', alta: '#b91c1c', 'sin dato': '#cbd5e1' };

function GanttTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const p = (payload.find((x: any) => x.dataKey === 'done') || payload[payload.length - 1]).payload;
  return (
    <div style={{ ...tooltipStyle, padding: 10 }}>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>{p.name}</div>
      <div>{p.desde} → {p.hasta}</div>
      <div>{p.doneTasks}/{p.total} tareas · {p.pct}% avance</div>
      {p.estado && <div>Estado: {PROYECTO_STATUS_LABELS[p.estado] || p.estado}</div>}
    </div>
  );
}

function ProyectoRow({ p }: { p: ProyectoProgreso }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="panel-flush proj-group">
      <button
        type="button"
        className="proj-head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, padding: 12 }}
      >
        <span aria-hidden style={{ transition: 'transform .15s', transform: open ? 'rotate(90deg)' : 'none' }}>▸</span>
        <span className="proj-name" style={{ fontWeight: 600 }}>{p.name}</span>
        {p.estado && (
          <span className="badge" style={{ background: PROYECTO_STATUS_COLORS[p.estado] || '#64748b', color: '#fff' }}>
            {PROYECTO_STATUS_LABELS[p.estado] || p.estado}
          </span>
        )}
        <span className="spacer" style={{ flex: 1 }} />
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 200 }}>
          <span style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--border)', overflow: 'hidden' }}>
            <span style={{ display: 'block', width: `${p.pct}%`, height: '100%', background: DONE_COLOR }} />
          </span>
          <span className="num" style={{ minWidth: 42 }}>{p.pct}%</span>
        </span>
        <span className="muted" style={{ minWidth: 70, textAlign: 'right' }}>{p.total} {p.total === 1 ? 'tarea' : 'tareas'}</span>
      </button>
      {open && (
        <div className="row" style={{ flexWrap: 'wrap', gap: 8, padding: '0 12px 12px 34px' }}>
          {p.porEstado.map((e) => (
            <div key={e.name} className="panel" style={{ padding: '8px 12px', minWidth: 120, borderLeft: `3px solid ${STATUS_COLORS[e.name] || '#64748b'}` }}>
              <div className="muted" style={{ fontSize: 12 }}>{STATUS_LABELS[e.name] || e.name}</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{e.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BoardProgreso({
  tasks,
  users,
  proyectoStatus,
  reviewEspacios,
}: {
  tasks: NormalizedTask[];
  users: UserDoc[];
  proyectoStatus?: Map<string, string>;
  reviewEspacios?: string[]; // si se pasa, el KPI de revisiones se limita a estos espacios
}) {
  const [sortBy, setSortBy] = useState<'tareas' | 'avance'>('tareas');
  const [groupByProj, setGroupByProj] = useState(false);

  const active = useMemo(() => cargaReal(tasks), [tasks]);
  const progreso = useMemo(() => progresoProyectos(tasks, proyectoStatus), [tasks, proyectoStatus]);

  const doneCount = useMemo(() => active.filter((t) => t.estado === 'done').length, [active]);
  const urgentes = useMemo(() => active.filter((t) => t.es_urgente).length, [active]);
  const pctAvance = active.length ? Math.round((doneCount / active.length) * 100) : 0;
  const pctUrg = active.length ? Math.round((urgentes / active.length) * 100) : 0;

  // El KPI de revisiones se acota a los espacios del responsable (si se indica).
  const revScope = useMemo(
    () => (reviewEspacios ? active.filter((t) => reviewEspacios.includes(t.espacio)) : active),
    [active, reviewEspacios]
  );
  const entregadas = useMemo(() => revScope.filter((t) => esEntregado(t.estado)).length, [revScope]);
  const rev = useMemo(() => revisionKpis(revScope), [revScope]);
  const pctPendientes = entregadas ? Math.round((rev.pendientes / entregadas) * 100) : 0;

  const proyectosSorted = useMemo(
    () => [...progreso].sort((a, b) => (sortBy === 'avance' ? b.pct - a.pct : b.total - a.total)),
    [progreso, sortBy]
  );

  const dificultad = useMemo(() => countBy(active, (t) => t.dificultad || 'sin dato'), [active]);

  // Gantt temporal con relleno de avance + línea "hoy".
  const gantt = useMemo(() => {
    const act = progreso.filter((p) => p.startI != null && p.endI != null);
    if (!act.length) return { rows: [] as any[], minI: 0, months: 0, hoyOffset: null as number | null };
    const nowI = monthIndex(new Date().toISOString())!;
    const lo = Math.min(nowI, ...act.map((p) => p.startI!));
    const hi = Math.max(nowI, ...act.map((p) => p.endI!));
    const rows = [...act]
      .sort((a, b) => (a.startI! - b.startI!) || (b.endI! - a.endI!))
      .map((p) => {
        const dur = (p.endI! - p.startI!) + 1;
        const done = Math.round(dur * (p.pct / 100) * 100) / 100;
        return {
          name: p.name, offset: p.startI! - lo, done, rem: Math.round((dur - done) * 100) / 100,
          estado: p.estado, pct: p.pct, doneTasks: p.done, total: p.total,
          desde: monthShortLabel(p.startI!), hasta: monthShortLabel(p.endI!),
        };
      });
    return { rows, minI: lo, months: hi - lo + 1, hoyOffset: nowI - lo + 0.5 };
  }, [progreso]);

  // Evolutivos mensuales.
  const serieProj = useMemo(() => serieMensualPorProyecto(tasks, 8), [tasks]);
  const serieTipo = useMemo(() => serieMensualPorTipo(tasks), [tasks]);

  const detalle = useMemo(
    () => [...active].sort((a, b) => (b.deadline || '').localeCompare(a.deadline || '')),
    [active]
  );
  // Agrupación por proyecto para la tabla; "(sin proyecto)" al final.
  const grupos = useMemo(() => {
    const m = new Map<string, NormalizedTask[]>();
    for (const t of detalle) {
      const k = t.proyecto || '(sin proyecto)';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(t);
    }
    return [...m.entries()].sort((a, b) =>
      a[0] === '(sin proyecto)' ? 1 : b[0] === '(sin proyecto)' ? -1 : b[1].length - a[1].length
    );
  }, [detalle]);
  const csvRows = detalle.map((t) => ({
    titulo: t.titulo, proyecto: t.proyecto || '(sin proyecto)', espacio: t.espacio,
    estado: STATUS_LABELS[t.estado] || t.estado,
    tipo: t.tipo, dificultad: t.dificultad, deadline: t.deadline, entregado: t.fecha_entrega,
    responsable: nameByEmail(t.responsable, users),
  }));

  const cols = groupByProj ? 8 : 9;
  const renderRow = (t: NormalizedTask, showProyecto: boolean) => (
    <tr key={t.id}>
      <td>{t.titulo ? t.titulo.slice(0, 60) : '—'}</td>
      {showProyecto && <td>{t.proyecto || '—'}</td>}
      <td>{t.espacio || '—'}</td>
      <td><span className="badge" style={{ background: STATUS_COLORS[t.estado] || '#64748b', color: '#fff' }}>{STATUS_LABELS[t.estado] || t.estado}</span></td>
      <td>{t.tipo || '—'}</td>
      <td>{t.dificultad || '—'}</td>
      <td>{fmtDia(t.deadline)}</td>
      <td>{fmtDate(t.fecha_entrega)}</td>
      <td>{nameByEmail(t.responsable, users)}</td>
    </tr>
  );

  if (active.length === 0) {
    return (
      <div className="grid">
        <BoardIntro titulo="Progreso por espacio" objetivo="No hay tareas para los filtros actuales." />
        <div className="panel"><p className="muted">Sin datos en la vista. Ajustá los filtros o esperá a que se carguen tareas de tu espacio.</p></div>
      </div>
    );
  }

  return (
    <div className="grid">
      <BoardIntro
        titulo="Progreso por espacio"
        objetivo="Qué se hizo, cómo vienen los proyectos y cómo evolucionó el trabajo mes a mes."
        preguntas={[
          '¿Cuántos proyectos y tareas hay, y qué proporción es urgente?',
          '¿En qué estado y con qué avance está cada proyecto?',
          '¿Cómo evolucionó el ingreso de tareas en el tiempo?',
        ]}
      />

      <div className="cards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px,1fr))' }}>
        <Kpi label="Proyectos" value={String(progreso.length)} help="Proyectos distintos presentes en las tareas de la vista." />
        <Kpi label="Tareas" value={String(active.length)} help="Tareas activas (excluye canceladas, rechazadas y pendientes de aprobación)." />
        <Kpi label="% Urgentes" value={`${pctUrg}%`} sub={`${urgentes} de ${active.length}`} help="Urgencia alta o tipo extraordinaria." />
        <Kpi label="% Avance global" value={`${pctAvance}%`} sub={`${doneCount} finalizadas`} help="Tareas finalizadas sobre el total de la vista." />
        <Kpi
          label="Revisiones pendientes"
          value={String(rev.pendientes)}
          sub={entregadas ? `${pctPendientes}% de ${entregadas} entregadas` : 'sin entregas'}
          help={`Tareas finalizadas esperando aprobación o rechazo.${reviewEspacios ? ' Limitado a tus espacios.' : ''}`}
        />
      </div>

      <Card
        title="Proyectos: estado y avance"
        hint="Tocá un proyecto para ver el detalle de sus tareas por estado. La barra muestra el % de tareas finalizadas."
        action={
          <div className="metric-toggle" role="radiogroup" aria-label="Ordenar proyectos">
            <button type="button" role="radio" aria-checked={sortBy === 'tareas'} className={sortBy === 'tareas' ? 'active' : ''} onClick={() => setSortBy('tareas')}>Por tareas</button>
            <button type="button" role="radio" aria-checked={sortBy === 'avance'} className={sortBy === 'avance' ? 'active' : ''} onClick={() => setSortBy('avance')}>Por avance</button>
          </div>
        }
      >
        <div className="grid" style={{ gap: 6 }}>
          {proyectosSorted.map((p) => <ProyectoRow key={p.name} p={p} />)}
          {proyectosSorted.length === 0 && <p className="muted" style={{ margin: 0 }}>No hay proyectos en la vista.</p>}
        </div>
      </Card>

      <Card
        title="Línea de tiempo y avance de proyectos"
        hint="Cada barra abarca del primer mes de solicitud al último deadline/entrega. La parte verde es lo finalizado; la gris, lo pendiente. La línea marca el mes actual."
      >
        {gantt.rows.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>No hay proyectos con fechas en la vista.</p>
        ) : (
          <>
            <div className="row" style={{ gap: 16, fontSize: 12, marginBottom: 8 }}>
              <span className="row" style={{ alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: DONE_COLOR, display: 'inline-block' }} />Finalizado</span>
              <span className="row" style={{ alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: PENDING_COLOR, display: 'inline-block' }} />Pendiente</span>
            </div>
            <ResponsiveContainer width="100%" height={Math.max(200, gantt.rows.length * 40 + 60)}>
              <BarChart data={gantt.rows} layout="vertical" margin={{ left: 10, right: 24, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
                <XAxis
                  type="number" domain={[0, gantt.months]} allowDecimals={false}
                  ticks={Array.from({ length: gantt.months + 1 }, (_, i) => i)}
                  tickFormatter={(i: number) => monthShortLabel(gantt.minI + i)} tick={axisTick}
                />
                <YAxis type="category" dataKey="name" tick={axisTick} width={150} interval={0} />
                <Tooltip content={<GanttTooltip />} cursor={{ fill: CHART_CURSOR }} />
                {gantt.hoyOffset != null && <ReferenceLine x={gantt.hoyOffset} stroke="var(--danger)" strokeDasharray="4 3" label={{ value: 'hoy', position: 'top', fontSize: 11, fill: 'var(--danger)' }} />}
                <Bar dataKey="offset" stackId="g" fill="transparent" />
                <Bar dataKey="done" stackId="g" fill={DONE_COLOR} maxBarSize={22} />
                <Bar dataKey="rem" stackId="g" fill={PENDING_COLOR} radius={[0, 4, 4, 0]} maxBarSize={22} />
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </Card>

      <Card
        title="Evolutivo mensual por proyecto"
        hint="Tareas ingresadas cada mes (por fecha de solicitud), apiladas por proyecto. Los proyectos menores se agrupan en “Otros”."
      >
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={serieProj.data} margin={{ left: -10, right: 10, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
            <XAxis dataKey="mes" tick={axisTick} angle={-25} textAnchor="end" interval={0} height={50} />
            <YAxis allowDecimals={false} tick={axisTick} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: CHART_CURSOR }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {serieProj.proyectos.map((name, i) => (
              <Bar key={name} dataKey={name} stackId="p" fill={name === 'Otros' ? OTROS_COLOR : PALETTE[i % PALETTE.length]} maxBarSize={48} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <div className="cards" style={{ gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', alignItems: 'stretch' }}>
        <Card
          title="Evolutivo mensual: ordinarias vs extraordinarias"
          hint="Tareas ingresadas cada mes por tipo. Muestra el mix de carga planificada frente a imprevista."
        >
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={serieTipo} margin={{ left: -10, right: 10, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="mes" tick={axisTick} angle={-25} textAnchor="end" interval={0} height={50} />
              <YAxis allowDecimals={false} tick={axisTick} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: CHART_CURSOR }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="ordinaria" name="Ordinaria" stackId="t" fill="#005EB8" maxBarSize={48} />
              <Bar dataKey="extraordinaria" name="Extraordinaria" stackId="t" fill="#FF7F32" radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <DonutCard
          title="Tareas por dificultad"
          hint="Distribución de las tareas según su dificultad."
          data={dificultad}
          colors={dificultad.map((d) => DIFICULTAD_COLORS[d.name] || OTROS_COLOR)}
          height={280}
          pct
        />
      </div>

      <Card
        title="Detalle de tareas"
        action={
          <div className="row" style={{ gap: 8 }}>
            <button className="ghost sm" type="button" onClick={() => setGroupByProj((v) => !v)}>
              {groupByProj ? 'Ver lista' : 'Agrupar por proyecto'}
            </button>
            <ExportButton filename="progreso_detalle.csv" rows={csvRows} />
          </div>
        }
      >
        <div style={{ overflowX: 'auto', maxHeight: 380 }}>
          <table>
            <thead>
              <tr>
                <th>Título</th>
                {!groupByProj && <th>Proyecto</th>}
                <th>Espacio</th><th>Estado</th><th>Tipo</th><th>Dif.</th><th>Deadline</th><th>Entregado</th><th>Responsable</th>
              </tr>
            </thead>
            <tbody>
              {groupByProj
                ? grupos.map(([name, rows]) => (
                    <Fragment key={name}>
                      <tr className="subtotal-row">
                        <td colSpan={cols}>{name} · {rows.length} {rows.length === 1 ? 'tarea' : 'tareas'}</td>
                      </tr>
                      {rows.map((t) => renderRow(t, false))}
                    </Fragment>
                  ))
                : detalle.map((t) => renderRow(t, true))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
